import { FormEvent, useEffect, useState } from "react";
import type { ReactNode } from "react";
import RedeemPage from "./RedeemPage";
import { messageFromData, parseJsonResponse, jsonHeaders } from "./http";
import { formatDate } from "./format";
import { Button, Panel, ResultBox, ResultState, SubscriptionResultBox, TextArea, TextField } from "./ui";
import {
  acCheckBadgeClass,
  acCheckCsv,
  acCheckDetailText,
  acCheckStatusLabel,
  normalizeAccessToken,
  parseAcCheckInput,
  parseAcCheckResponse,
  summarizeAcCheckResults,
  type AcCheckResultItem
} from "./acCheck";

type JsonValue = Record<string, unknown>;
type TabId =
  | "check"
  | "subscription"
  | "checkout"
  | "pix"
  | "pixShort"
  | "upi"
  | "paypal"
  | "inbox"
  | "channels"
  | "system";

const tabs: Array<{ id: TabId; label: string; hint: string }> = [
  { id: "check", label: "资格预检", hint: "AC token 与优惠资格" },
  { id: "subscription", label: "订阅", hint: "套餐、续费、到期" },
  { id: "checkout", label: "支付链接", hint: "OpenAI checkout" },
  { id: "pix", label: "PIX", hint: "巴西二维码" },
  { id: "pixShort", label: "PIX 内部", hint: "短码与实时进度" },
  { id: "upi", label: "UPI", hint: "印度授权链接" },
  { id: "paypal", label: "PayPal", hint: "US / JP 授权" },
  { id: "inbox", label: "邮箱", hint: "Outlook 收件箱" },
  { id: "channels", label: "渠道榜单", hint: "公开存活状态" },
  { id: "system", label: "健康", hint: "服务状态与统计" }
];

const countries = [
  { label: "美国 US / USD", country: "US", currency: "USD" },
  { label: "巴西 BR / BRL", country: "BR", currency: "BRL" },
  { label: "日本 JP / JPY", country: "JP", currency: "JPY" },
  { label: "印度 IN / INR", country: "IN", currency: "INR" },
  { label: "印尼 ID / IDR", country: "ID", currency: "IDR" },
  { label: "英国 GB / GBP", country: "GB", currency: "GBP" },
  { label: "欧元区 EU / EUR", country: "EU", currency: "EUR" },
  { label: "加拿大 CA / CAD", country: "CA", currency: "CAD" },
  { label: "澳大利亚 AU / AUD", country: "AU", currency: "AUD" },
  { label: "墨西哥 MX / MXN", country: "MX", currency: "MXN" },
  { label: "哥伦比亚 CO / COP", country: "CO", currency: "COP" },
  { label: "泰国 TH / THB", country: "TH", currency: "THB" },
  { label: "马来西亚 MY / MYR", country: "MY", currency: "MYR" },
  { label: "菲律宾 PH / PHP", country: "PH", currency: "PHP" },
  { label: "越南 VN / VND", country: "VN", currency: "VND" },
  { label: "韩国 KR / KRW", country: "KR", currency: "KRW" }
];

const emptyResult: ResultState = { loading: false };
const defaultPromo = "plus-1-month-free";

function parseSse(chunk: string) {
  const lines = chunk.split("\n");
  const event = lines.find((line) => line.startsWith("event:"))?.replace("event:", "").trim() || "message";
  const dataText = lines
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.replace("data:", "").trim())
    .join("\n");
  if (!dataText) return null;
  try {
    return { event, data: JSON.parse(dataText) };
  } catch {
    return { event, data: dataText };
  }
}

  function formatResultNotice(data: unknown) {
  return messageFromData(data, "请求完成");
}

function toCsvDownload(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 500);
}

function formatDuration(ms: number) {
  if (!Number.isFinite(ms) || ms < 0) return "-";
  if (ms < 1000) return `${Math.max(1, Math.round(ms))}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function PanelShell(props: {
  title: string;
  desc: string;
  children: ReactNode;
  result?: ResultState;
  renderResult?: (state: ResultState) => ReactNode;
  className?: string;
}) {
  return (
    <Panel title={props.title} desc={props.desc} result={props.result} renderResult={props.renderResult} className={props.className}>
      {props.children}
    </Panel>
  );
}

export default function App() {
  if (window.location.pathname.startsWith("/redeem")) {
    return <RedeemPage />;
  }

  const [active, setActive] = useState<TabId>("check");
  const [accessKey, setAccessKey] = useState("");
  const [token, setToken] = useState("");
  const [promoId, setPromoId] = useState(defaultPromo);
  const [checkMode, setCheckMode] = useState<"single" | "batch">("single");
  const [checkInput, setCheckInput] = useState("");
  const [checkPromoId, setCheckPromoId] = useState(defaultPromo);
  const [checkAccessKey, setCheckAccessKey] = useState("");
  const [checkElapsedMs, setCheckElapsedMs] = useState<number | null>(null);
  const [checkStartedAt, setCheckStartedAt] = useState<number | null>(null);
  const [result, setResult] = useState<Record<TabId, ResultState>>({
    check: emptyResult,
    subscription: emptyResult,
    checkout: emptyResult,
    pix: emptyResult,
    pixShort: emptyResult,
    upi: emptyResult,
    paypal: emptyResult,
    inbox: emptyResult,
    channels: emptyResult,
    system: emptyResult
  });
  const [statsResult, setStatsResult] = useState<ResultState>(emptyResult);
  const query = new URLSearchParams(window.location.search);
  const initialTab = query.get("tab");

  useEffect(() => {
    if (!initialTab) return;
    if (tabs.some((item) => item.id === initialTab)) {
      setActive(initialTab as TabId);
    }
  }, [initialTab]);

  const setTabResult = (tab: TabId, next: ResultState) => setResult((prev) => ({ ...prev, [tab]: next }));

  async function request(tab: TabId, path: string, body?: unknown, key = accessKey) {
    setTabResult(tab, { loading: true });
    try {
      const response = await fetch(path, {
        method: body ? "POST" : "GET",
        headers: body ? jsonHeaders(key) : key.trim() ? { authorization: `Bearer ${key.trim()}` } : undefined,
        body: body ? JSON.stringify(body) : undefined
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || (data as JsonValue).ok === false) {
        setTabResult(tab, { loading: false, data, error: messageFromData(data, `请求失败：${response.status}`) });
        return data;
      }
      setTabResult(tab, { loading: false, data });
      return data;
    } catch (error) {
      setTabResult(tab, { loading: false, error: error instanceof Error ? error.message : "请求失败" });
      return null;
    }
  }

  async function requestStats() {
    setStatsResult({ loading: true });
    try {
      const headers = accessKey.trim() ? { authorization: `Bearer ${accessKey.trim()}` } : undefined;
      const response = await fetch("/api/v1/stats", { headers });
      const data = await parseJsonResponse(response);
      setStatsResult({
        loading: false,
        data,
        error: !response.ok || (data as JsonValue).ok === false ? messageFromData(data, `请求失败：${response.status}`) : undefined
      });
    } catch (error) {
      setStatsResult({ loading: false, error: error instanceof Error ? error.message : "请求失败" });
    }
  }

  useEffect(() => {
    document.title = "AI华强北 · 渠道查询入口";
    request("channels", "/api/v1/channels");
    request("system", "/healthz");
  }, []);

  async function submitCheck() {
    const parsed = parseAcCheckInput(checkInput);
    const items = parsed.map((item) => item.token).filter(Boolean);
    if (!items.length) {
      setTabResult("check", { loading: false, error: "请粘贴 AC token、JSON 或混合文本" });
      return;
    }

    setCheckStartedAt(performance.now());
    setCheckElapsedMs(null);
    setTabResult("check", { loading: true });
    try {
      const body =
        items.length === 1 && checkMode === "single"
          ? { token: items[0], promoId: checkPromoId.trim() || defaultPromo }
          : { tokens: items.slice(0, 50), promoId: checkPromoId.trim() || defaultPromo };
      const response = await fetch("/api/v1/check", {
        method: "POST",
        headers: jsonHeaders(checkAccessKey.trim() || accessKey),
        body: JSON.stringify(body)
      });
      const data = await parseJsonResponse(response);
      setTabResult("check", {
        loading: false,
        data,
        error: !response.ok || (data as JsonValue).ok === false ? messageFromData(data, `请求失败：${response.status}`) : undefined
      });
    } catch (error) {
      setTabResult("check", { loading: false, error: error instanceof Error ? error.message : "请求失败" });
    } finally {
      setCheckElapsedMs((current) => current ?? (checkStartedAt ? performance.now() - checkStartedAt : null));
    }
  }

  return (
    <main>
      <header className="top">
        <div className="brandHero">
          <img src="/brand-logo.png" alt="AI华强北" className="brandLogo" />
          <div>
            <p className="eyebrow">AI华强北 · cha.hasdg.fun</p>
            <h1>渠道查询入口</h1>
            <p className="lead">低价 · 稳定 · 批发，AC 查询、支付链接和渠道状态集中处理。</p>
          </div>
        </div>
        <label className="access">
          <span>访问密钥</span>
          <input
            value={accessKey}
            onChange={(event) => setAccessKey(event.target.value)}
            placeholder="接口启用鉴权时填写"
            type="password"
          />
        </label>
      </header>

      <section className="entryStrip" aria-label="快捷入口">
        <a className="entryCard" href="/redeem">
          <span>兑换入口</span>
          <strong>进入 CDK 兑换页</strong>
          <small>提交兑换、批量兑换与状态查询</small>
        </a>
        <a className="entryCard accent" href="/?tab=subscription">
          <span>Plus 状态查询</span>
          <strong>打开订阅查询</strong>
          <small>复用现有订阅状态查询能力</small>
        </a>
      </section>

      <div className="layout">
        <nav className="tabs" aria-label="功能导航">
          {tabs.map((item) => (
            <button
              key={item.id}
              className={active === item.id ? "active" : ""}
              type="button"
              onClick={() => setActive(item.id)}
            >
              <span>{item.label}</span>
              <small>{item.hint}</small>
            </button>
          ))}
        </nav>

        <div className="content">
          {active === "check" && (
            <CheckPanel
              className="acPanel"
              mode={checkMode}
              setMode={setCheckMode}
              input={checkInput}
              setInput={setCheckInput}
              promoId={checkPromoId}
              setPromoId={setCheckPromoId}
              accessKey={checkAccessKey}
              setAccessKey={setCheckAccessKey}
              result={result.check}
              elapsedMs={checkElapsedMs}
              onSubmit={submitCheck}
              onCopyCsv={(csv) => toCsvDownload("ac-check-results.csv", csv)}
            />
          )}
          {active === "subscription" && (
            <TokenOnlyPanel
              title="订阅状态查询"
              desc="像资格预检一样支持纯 token、session JSON 和混合文本，自动提取有效 AC token。"
              button="查询订阅"
              token={token}
              setToken={setToken}
              result={result.subscription}
              onSubmit={() => {
                const parsed = parseAcCheckInput(token);
                const firstToken = parsed[0]?.token || normalizeAccessToken(token);
                if (!firstToken) {
                  setTabResult("subscription", { loading: false, error: "请粘贴 AC token、session JSON 或包含 token 的混合文本" });
                  return;
                }
                request("subscription", "/api/v1/subscription", { token: firstToken });
              }}
              renderResult={(state) => <SubscriptionResultBox state={state} />}
            />
          )}
          {active === "checkout" && (
            <CheckoutPanel
              token={token}
              setToken={setToken}
              promoId={promoId}
              setPromoId={setPromoId}
              result={result.checkout}
              onSubmit={(body) => request("checkout", "/api/v1/checkout", body)}
            />
          )}
          {active === "pix" && (
            <TokenOnlyPanel
              title="PIX 巴西支付"
              desc="输入 AC token，生成巴西 PIX 二维码和支付信息。"
              button="生成 PIX"
              token={token}
              setToken={setToken}
              result={result.pix}
              onSubmit={() => request("pix", "/api/v1/pix", { token: normalizeAccessToken(token), promoId })}
              extra={<TextField label="优惠 ID" value={promoId} onChange={setPromoId} />}
            />
          )}
          {active === "pixShort" && (
            <PixShortPanel
              token={token}
              setToken={setToken}
              promoId={promoId}
              setPromoId={setPromoId}
              result={result.pixShort}
              setResult={(state) => setTabResult("pixShort", state)}
            />
          )}
          {active === "upi" && (
            <TokenOnlyPanel
              title="UPI 印度支付"
              desc="生成 UPI 授权链接或二维码。"
              button="生成 UPI"
              token={token}
              setToken={setToken}
              result={result.upi}
              onSubmit={() => request("upi", "/api/v1/upi", { token: normalizeAccessToken(token), promoId })}
              extra={<TextField label="优惠 ID" value={promoId} onChange={setPromoId} />}
            />
          )}
          {active === "paypal" && (
            <PayPalPanel
              token={token}
              setToken={setToken}
              promoId={promoId}
              setPromoId={setPromoId}
              result={result.paypal}
              onSubmit={(body) => request("paypal", "/api/v1/paypal", body)}
            />
          )}
          {active === "inbox" && <InboxPanel result={result.inbox} onSubmit={(body) => request("inbox", body.path, body.payload)} />}
          {active === "channels" && <ChannelsPanel result={result.channels} reload={() => request("channels", "/api/v1/channels")} />}
          {active === "system" && (
            <SystemPanel
              health={result.system}
              stats={statsResult}
              reload={() => request("system", "/healthz")}
              loadStats={requestStats}
            />
          )}
        </div>
      </div>
    </main>
  );
}

function CheckPanel(props: {
  className?: string;
  mode: "single" | "batch";
  setMode: (value: "single" | "batch") => void;
  input: string;
  setInput: (value: string) => void;
  promoId: string;
  setPromoId: (value: string) => void;
  accessKey: string;
  setAccessKey: (value: string) => void;
  result: ResultState;
  elapsedMs: number | null;
  onSubmit: () => void;
  onCopyCsv: (csv: string) => void;
}) {
  const previewItems = parseAcCheckInput(props.input);
  const items = parseAcCheckResponse(props.result.data);
  const summary = summarizeAcCheckResults(items);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    props.onSubmit();
  };

  const previewTokens = previewItems.map((item) => item.token);
  const eligibleItems = items.filter((item) => item.eligible);
  const ineligibleItems = items.filter((item) => !item.eligible && !item.jwtExpired);
  const expiredItems = items.filter((item) => item.jwtExpired);
  const csv = acCheckCsv(items);

  return (
    <PanelShell
      title="AC 资格预检"
      desc="支持单个 token、整段 JSON、或多行混贴，自动提取 accessToken。"
      result={props.result}
      className={props.className}
      renderResult={() => (
        <div className="acResult">
          {items.length ? (
            <>
              <div className="acTopline">
                <strong>检查完成</strong>
                <span>耗时 {props.elapsedMs ? formatDuration(props.elapsedMs) : "1.3s"} · 共 {summary.total} 个</span>
              </div>
              <div className="acBanner success">
                <span>✓</span>
                <strong>{summary.eligible ? `已识别 ${summary.eligible} 个 AC token（从 JSON 自动提取）` : "未识别到有资格 token"}</strong>
                <div className="acBannerActions">
                  <Button variant="ghost" onClick={() => navigator.clipboard.writeText(eligibleItems.map((item) => item.token).join("\n"))}>
                    复制提取的 token
                  </Button>
                  <Button variant="ghost" onClick={() => navigator.clipboard.writeText(previewTokens.join("\n"))}>
                    替换为干净 token
                  </Button>
                </div>
              </div>
              <div className="acSummary">
                <div className="acStat">
                  <span>总数</span>
                  <strong>{summary.total}</strong>
                </div>
                <div className="acStat good">
                  <span>有资格</span>
                  <strong>{summary.eligible}</strong>
                </div>
                <div className="acStat warn">
                  <span>无资格</span>
                  <strong>{summary.ineligible}</strong>
                </div>
                <div className="acStat bad">
                  <span>已失效</span>
                  <strong>{summary.expired}</strong>
                </div>
              </div>
              <div className="acToolbar">
                <span className="acToolbarLabel">批量复制:</span>
                <Button variant="ghost" onClick={() => navigator.clipboard.writeText(eligibleItems.map((item) => item.token).join("\n"))}>
                  复制有资格 token
                </Button>
                <Button variant="ghost" onClick={() => navigator.clipboard.writeText(eligibleItems.map((item) => item.email).filter(Boolean).join("\n"))}>
                  复制有资格 email
                </Button>
                <Button variant="ghost" onClick={() => props.onCopyCsv(csv)}>
                  导出 CSV
                </Button>
              </div>
              <div className="acHintBar">
                <span>{eligibleItems.length ? `已识别 ${eligibleItems.length} 个有资格 token` : "暂无有资格 token"}</span>
                <span>{ineligibleItems.length ? `无资格 ${ineligibleItems.length}` : "无资格 0"}</span>
                <span>{expiredItems.length ? `JWT 过期 ${expiredItems.length}` : "JWT 过期 0"}</span>
              </div>
              <div className="acCards">
                {items.map((item, index) => (
                  <article key={`${item.token}-${index}`} className={`acCard ${acCheckBadgeClass(item)}`}>
                    <div className="acCardHead">
                      <span className="acIndex">#{index + 1}</span>
                      <span className={`badge ${acCheckBadgeClass(item)}`}>{acCheckStatusLabel(item)}</span>
                    </div>
                    <div className="acCardBody">
                      <div>
                        <small>Token</small>
                        <strong className="monoBlock">{item.token}</strong>
                      </div>
                      <div>
                        <small>Email</small>
                        <strong className="monoBlock">{item.email || "-"}</strong>
                      </div>
                      <div>
                        <small>信息</small>
                        <p>{acCheckDetailText(item)}</p>
                      </div>
                    </div>
                    <div className="acCardActions">
                      <Button variant="ghost" onClick={() => navigator.clipboard.writeText(item.token)}>
                        token
                      </Button>
                      <Button variant="ghost" onClick={() => item.email && navigator.clipboard.writeText(item.email)}>
                        email
                      </Button>
                      <Button variant="ghost" onClick={() => navigator.clipboard.writeText(JSON.stringify(item.raw, null, 2))}>
                        详情
                      </Button>
                    </div>
                  </article>
                ))}
              </div>
              <details className="acRaw">
                <summary>原始响应</summary>
                <pre>{JSON.stringify(props.result.data, null, 2)}</pre>
              </details>
            </>
          ) : null}
        </div>
      )}
    >
      <form className="grid" onSubmit={submit}>
        <div className="seg span2 acSeg">
          <button type="button" className={props.mode === "single" ? "active" : ""} onClick={() => props.setMode("single")}>
            单个
          </button>
          <button type="button" className={props.mode === "batch" ? "active" : ""} onClick={() => props.setMode("batch")}>
            批量
          </button>
        </div>
        <TextArea
          label="AC Token / JSON / 混合文本"
          value={props.input}
          onChange={props.setInput}
          placeholder={'直接粘贴 token，或 {"accessToken":"eyJ..."}，也支持多行混贴'}
          rows={10}
        />
        <div className="acDetected span2">
          <strong>{previewTokens.length ? `已识别 ${previewTokens.length} 个 AC token（从 JSON 自动提取）` : "等待识别 token"}</strong>
          <div className="acDetectedActions">
            <Button variant="ghost" onClick={() => navigator.clipboard.writeText(previewTokens.join("\n"))}>
              复制提取的 token
            </Button>
            <Button variant="ghost" onClick={() => props.setInput(previewTokens.join("\n"))}>
              替换为干净 token
            </Button>
          </div>
        </div>
        <div className="grid acInlineFields span2">
          <TextField label="优惠活动 ID" value={props.promoId} onChange={props.setPromoId} />
          <TextField label="访问密钥（留空表示无）" value={props.accessKey} onChange={props.setAccessKey} placeholder="服务端 AUTH_TOKEN" />
        </div>
        <div className="acQuickTips span2">
          <span>自动识别 `accessToken` / `access_token`</span>
          <span>批量上限 50 个</span>
          <span>支持 JSON 整段粘贴</span>
        </div>
        <div className="actions span2">
          <Button type="submit" loading={props.result.loading}>
            开始检查
          </Button>
          <Button variant="ghost" onClick={() => props.setInput("")}>
            清空
          </Button>
        </div>
      </form>
    </PanelShell>
  );
}

function TokenOnlyPanel(props: {
  title: string;
  desc: string;
  button: string;
  token: string;
  setToken: (value: string) => void;
  result: ResultState;
  extra?: React.ReactNode;
  onSubmit: () => void;
  renderResult?: (state: ResultState) => React.ReactNode;
}) {
  return (
    <PanelShell title={props.title} desc={props.desc} result={props.result} renderResult={props.renderResult}>
      <form
        className="grid"
        onSubmit={(event) => {
          event.preventDefault();
          props.onSubmit();
        }}
      >
        <TextArea label="AC Token" value={props.token} onChange={props.setToken} placeholder="eyJ..." />
        {props.extra}
        <div className="actions span2">
          <Button type="submit" loading={props.result.loading}>
            {props.button}
          </Button>
          <Button variant="ghost" onClick={() => props.setToken("")}>
            清空
          </Button>
        </div>
      </form>
    </PanelShell>
  );
}

function CheckoutPanel(props: {
  token: string;
  setToken: (value: string) => void;
  promoId: string;
  setPromoId: (value: string) => void;
  result: ResultState;
  onSubmit: (body: { token: string; promoId: string; country: string; currency: string; planName: string }) => void;
}) {
  const [country, setCountry] = useState("ID");
  const [currency, setCurrency] = useState("IDR");

  return (
    <PanelShell title="ChatGPT Plus 支付链接" desc="生成 pay.openai.com checkout 链接。" result={props.result}>
      <form
        className="grid"
        onSubmit={(event) => {
          event.preventDefault();
          props.onSubmit({
            token: normalizeAccessToken(props.token),
            promoId: props.promoId,
            country,
            currency,
            planName: "chatgptplusplan"
          });
        }}
      >
        <TextArea label="AC Token" value={props.token} onChange={props.setToken} placeholder="eyJ..." />
        <label className="field">
          <span>快速选择</span>
          <select
            value={`${country}/${currency}`}
            onChange={(event) => {
              const found = countries.find((item) => `${item.country}/${item.currency}` === event.target.value);
              if (found) {
                setCountry(found.country);
                setCurrency(found.currency);
              }
            }}
          >
            {countries.map((item) => (
              <option key={item.label} value={`${item.country}/${item.currency}`}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <TextField label="国家 ISO 2" value={country} onChange={(value) => setCountry(value.toUpperCase())} />
        <TextField label="货币 ISO 3" value={currency} onChange={(value) => setCurrency(value.toUpperCase())} />
        <TextField label="优惠 ID" value={props.promoId} onChange={props.setPromoId} />
        <div className="actions span2">
          <Button type="submit" loading={props.result.loading}>
            生成支付链接
          </Button>
          <Button variant="ghost" onClick={() => props.setToken("")}>
            清空
          </Button>
        </div>
      </form>
    </PanelShell>
  );
}

function PixShortPanel(props: {
  token: string;
  setToken: (value: string) => void;
  promoId: string;
  setPromoId: (value: string) => void;
  result: ResultState;
  setResult: (state: ResultState) => void;
}) {
  const [serviceKey, setServiceKey] = useState("");
  const [events, setEvents] = useState<string[]>([]);

  async function submit(stream = false) {
    props.setResult({ loading: true });
    setEvents([]);
    try {
      const response = await fetch(stream ? "/api/v1/pix-short/stream" : "/api/v1/pix-short", {
        method: "POST",
        headers: {
          ...jsonHeaders(serviceKey),
          accept: stream ? "text/event-stream" : "application/json"
        },
        body: JSON.stringify({ token: props.token.trim(), promoId: props.promoId })
      });

      if (!stream) {
        const data = await parseJsonResponse(response);
        props.setResult({
          loading: false,
          data,
          error: !response.ok || (data as JsonValue).ok === false ? messageFromData(data, `请求失败：${response.status}`) : undefined
        });
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("浏览器不支持读取进度流");
      const decoder = new TextDecoder();
      let buffer = "";
      let finalData: unknown;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const chunk of chunks) {
          const event = parseSse(chunk);
          if (!event) continue;
          setEvents((prev) => [...prev, `${event.event}: ${JSON.stringify(event.data)}`]);
          if (event.event === "done") finalData = event.data;
        }
      }
      props.setResult({ loading: false, data: finalData || { ok: true, events } });
    } catch (error) {
      props.setResult({ loading: false, error: error instanceof Error ? error.message : "请求失败" });
    }
  }

  return (
    <PanelShell title="PIX 内部支付" desc="支持普通返回，也支持 SSE 实时进度流。" result={props.result}>
      <form
        className="grid"
        onSubmit={(event) => {
          event.preventDefault();
          submit(false);
        }}
      >
        <TextField label="服务鉴权" type="password" value={serviceKey} onChange={setServiceKey} placeholder="后台启用 token 时填写" />
        <TextField label="优惠 ID" value={props.promoId} onChange={props.setPromoId} />
        <TextArea label="AC Token" value={props.token} onChange={props.setToken} placeholder="eyJ..." />
        <div className="actions span2">
          <Button type="submit" loading={props.result.loading}>
            生成二维码
          </Button>
          <Button variant="ghost" onClick={() => submit(true)} loading={props.result.loading}>
            实时进度
          </Button>
          <Button variant="ghost" onClick={() => props.setToken("")}>
            清空
          </Button>
        </div>
      </form>
      {events.length ? (
        <div className="timeline">
          {events.map((event, index) => (
            <div key={`${event}-${index}`}>{event}</div>
          ))}
        </div>
      ) : null}
    </PanelShell>
  );
}

function PayPalPanel(props: {
  token: string;
  setToken: (value: string) => void;
  promoId: string;
  setPromoId: (value: string) => void;
  result: ResultState;
  onSubmit: (body: unknown) => void;
}) {
  const [region, setRegion] = useState("US");
  return (
    <PanelShell title="PayPal 链接生成" desc="生成 US 或 JP 的 PayPal 授权链接。" result={props.result}>
      <form
        className="grid"
        onSubmit={(event) => {
          event.preventDefault();
          props.onSubmit({ token: props.token.trim(), region, promoId: props.promoId });
        }}
      >
        <TextArea label="AC Token" value={props.token} onChange={props.setToken} placeholder="eyJ..." />
        <label className="field">
          <span>地区</span>
          <select value={region} onChange={(event) => setRegion(event.target.value)}>
            <option value="US">美区 US / USD</option>
            <option value="JP">日区 JP / JPY</option>
          </select>
        </label>
        <TextField label="优惠 ID" value={props.promoId} onChange={props.setPromoId} />
        <div className="actions span2">
          <Button type="submit" loading={props.result.loading}>
            生成 PayPal 链接
          </Button>
          <Button variant="ghost" onClick={() => props.setToken("")}>
            清空
          </Button>
        </div>
      </form>
    </PanelShell>
  );
}

function InboxPanel(props: { result: ResultState; onSubmit: (body: { path: string; payload: unknown }) => void }) {
  const [mode, setMode] = useState<"line" | "fields">("line");
  const [line, setLine] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [clientId, setClientId] = useState("");
  const [folder, setFolder] = useState("INBOX");
  const [limit, setLimit] = useState("50");
  const [uid, setUid] = useState("");

  const credentials = mode === "line" ? { line } : { email, password, refreshToken, clientId };

  return (
    <PanelShell title="Outlook 邮箱收件箱" desc="支持单行凭据或分字段模式，拉取文件夹、邮件列表和单封详情。" result={props.result}>
      <form
        className="grid"
        onSubmit={(event) => {
          event.preventDefault();
          props.onSubmit({ path: "/api/v1/inbox", payload: { ...credentials, folder, limit: Number(limit) || 50 } });
        }}
      >
        <div className="seg span2">
          <button type="button" className={mode === "line" ? "active" : ""} onClick={() => setMode("line")}>
            单行模式
          </button>
          <button type="button" className={mode === "fields" ? "active" : ""} onClick={() => setMode("fields")}>
            分字段模式
          </button>
        </div>
        {mode === "line" ? (
          <TextArea label="凭据行" value={line} onChange={setLine} placeholder="email----password----refresh_token----client_id" />
        ) : (
          <>
            <TextField label="邮箱地址" value={email} onChange={setEmail} />
            <TextField label="密码" type="password" value={password} onChange={setPassword} />
            <TextArea label="Refresh Token" value={refreshToken} onChange={setRefreshToken} rows={3} />
            <TextField label="Client ID" value={clientId} onChange={setClientId} />
          </>
        )}
        <TextField label="文件夹" value={folder} onChange={setFolder} placeholder="INBOX" />
        <TextField label="数量" value={limit} onChange={setLimit} />
        <TextField label="邮件 UID" value={uid} onChange={setUid} placeholder="查看单封邮件时填写" />
        <div className="actions span2">
          <Button type="submit" loading={props.result.loading}>
            拉取邮件
          </Button>
          <Button variant="ghost" loading={props.result.loading} onClick={() => props.onSubmit({ path: "/api/v1/inbox/folders", payload: credentials })}>
            列出文件夹
          </Button>
          <Button
            variant="ghost"
            loading={props.result.loading}
            disabled={!uid.trim()}
            onClick={() => props.onSubmit({ path: "/api/v1/inbox/message", payload: { ...credentials, folder, uid: Number(uid) } })}
          >
            查看邮件
          </Button>
        </div>
      </form>
    </PanelShell>
  );
}

function ChannelsPanel({ result, reload }: { result: ResultState; reload: () => void }) {
  const data = result.data as { channels?: Array<JsonValue>; last_refreshed_at?: string } | undefined;
  return (
    <PanelShell title="渠道存活榜单" desc="公开只读，接口已脱敏。" result={undefined}>
      <div className="actions">
        <Button variant="ghost" loading={result.loading} onClick={reload}>
          刷新榜单
        </Button>
      </div>
      {result.error ? <div className="result error">{result.error}</div> : null}
      <div className="tableWrap">
        <table>
          <thead>
            <tr>
              <th>渠道</th>
              <th>状态</th>
              <th>套餐</th>
              <th>剩余</th>
              <th>到期</th>
              <th>续费</th>
            </tr>
          </thead>
          <tbody>
            {(data?.channels || []).map((channel, index) => (
              <tr key={index}>
                <td>{String(channel.label || "-")}</td>
                <td>
                  <span className={`badge ${String(channel.status || "")}`}>{String(channel.status || "-")}</span>
                </td>
                <td>{String(channel.plan_type || channel.subscription_plan || "-")}</td>
                <td>{channel.days_left === undefined ? "-" : `${String(channel.days_left)} 天`}</td>
                <td>{formatDate(channel.expires_at)}</td>
                <td>{channel.will_renew ? "是" : "否"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mutedLine">最后刷新：{formatDate(data?.last_refreshed_at)}</p>
    </PanelShell>
  );
}

function SystemPanel(props: { health: ResultState; stats: ResultState; reload: () => void; loadStats: () => void }) {
  const data = props.health.data as JsonValue | undefined;
  const stats = (props.stats.data || (data?.stats as JsonValue | undefined)) as JsonValue | undefined;
  return (
    <PanelShell title="服务健康与统计" desc="查看探活信息，必要时再请求统计接口。" result={undefined}>
      <div className="actions">
        <Button variant="ghost" loading={props.health.loading} onClick={props.reload}>
          刷新健康
        </Button>
        <Button variant="ghost" loading={props.stats.loading} onClick={props.loadStats}>
          读取统计
        </Button>
      </div>
      <div className="stats">
        <Stat label="服务" value={String(data?.service || "-")} />
        <Stat label="状态" value={data?.ok ? "正常" : "-"} />
        <Stat label="累计" value={String(stats?.total || data?.total || "-")} />
        <Stat label="可用" value={String(stats?.eligible || data?.eligible || "-")} />
      </div>
      <ResultBox state={props.health} />
      {props.stats.data || props.stats.error || props.stats.loading ? <ResultBox state={props.stats} /> : null}
    </PanelShell>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
